/**
 * Equalize heights of items per visual row.
 *
 * Add `data-equal-height` to a wrapper element. Its direct children
 * are grouped by their actual top position, so the row size adapts
 * automatically to whatever column count the layout produces at the
 * current breakpoint. The tallest item in each row sets the height
 * for the rest.
 *
 * Optional: set `data-equal-height-items="<selector>"` on the wrapper
 * to target descendants other than direct children.
 *
 * Re-run from anywhere by dispatching `equal-heights-refresh` on
 * `document`, or by importing and calling `initEqualHeights()`
 * (e.g. after a FacetWP refresh).
 */

const RESIZE_DEBOUNCE = 100;
let resizeTimer;
let listenersBound = false;

function getItems(container) {
	const selector = container.dataset.equalHeightItems;

	if (selector) {
		return Array.from(container.querySelectorAll(selector));
	}

	return Array.from(container.children);
}

function equalize(container) {
	const items = getItems(container);

	if (items.length < 2) {
		return;
	}

	items.forEach((item) => {
		item.style.height = '';
	});

	const rows = new Map();

	items.forEach((item) => {
		const top = Math.round(item.getBoundingClientRect().top + window.scrollY);

		if (!rows.has(top)) {
			rows.set(top, []);
		}

		rows.get(top).push(item);
	});

	rows.forEach((rowItems) => {
		if (rowItems.length < 2) {
			return;
		}

		const max = rowItems.reduce((tallest, el) => {
			return Math.max(tallest, el.getBoundingClientRect().height);
		}, 0);

		rowItems.forEach((item) => {
			item.style.height = `${max}px`;
		});
	});
}

function refreshAll() {
	document.querySelectorAll('[data-equal-height]').forEach((container) => equalize(container));
}

function bindListeners() {
	if (listenersBound) {
		return;
	}

	window.addEventListener('resize', () => {
		clearTimeout(resizeTimer);
		resizeTimer = setTimeout(refreshAll, RESIZE_DEBOUNCE);
	});

	window.addEventListener('load', refreshAll);
	document.addEventListener('equal-heights-refresh', refreshAll);

	listenersBound = true;
}

export function initEqualHeights(scope = document) {
	scope.querySelectorAll('[data-equal-height]').forEach((container) => equalize(container));
	bindListeners();
}
